/** Luồng AI gợi ý cho một bước: đề xuất (stream) → xem diff → chọn/sửa → Áp dụng → Hoàn tác. */
import { useCallback, useRef, useState } from 'react';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import { AiHubStreamError } from '@/services/knowledge/ai-agent-stream';
import { agentRunClient, streamRun } from '@/services/knowledge/agent-run-client';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { AiHubEvent, AiProposal } from '@/types/ai-agent';
import { agentLabel, type ChatTimelineItem } from '@/components/common/AiChat/useAiChat';
import type { ApplyResult, DiffRow, StepAdapter } from './adapters/types';
import { markStepChanged } from './consistency';

export type AssistantPhase = 'idle' | 'running' | 'review' | 'applying' | 'applied';

export interface UseAiStepAssistant {
  phase: AssistantPhase;
  timeline: ChatTimelineItem[];
  proposal: AiProposal | null;
  rows: DiffRow[];
  selected: Set<string>;
  edits: Record<string, string>;
  error: string | null;
  /** `extraSnapshot` gộp vào snapshot của adapter (vd. B5 chỉ một đầu cá). */
  propose: (instruction: string, extraSnapshot?: Record<string, unknown>) => Promise<void>;
  resume: () => Promise<boolean>;
  stop: () => void;
  toggle: (ref: string, on?: boolean) => void;
  selectAll: (on: boolean) => void;
  edit: (ref: string, text: string) => void;
  apply: (opts?: { all?: boolean }) => Promise<ApplyResult | null>;
  undo: (result: ApplyResult) => Promise<void>;
  reject: () => Promise<void>;
  reset: () => void;
}

const defaultSelection = (rows: DiffRow[]) => new Set(rows.filter((r) => r.op !== 'delete' && !r.warning).map((r) => r.ref));

export type UseAiStepAssistantOptions = {
  onApplied?: () => void;
  /** Đẩy proposal thẳng lên UI trang — không màn review. */
  onProposalToPage?: (proposal: AiProposal) => Promise<void>;
  /** Giữ timeline trong popover sau khi ghi row (stream UX). */
  preserveTimelineOnPageApply?: boolean;
};

export const useAiStepAssistant = <Cur,>(
  adapter: StepAdapter<Cur>,
  options?: UseAiStepAssistantOptions | (() => void),
): UseAiStepAssistant => {
  const opts: UseAiStepAssistantOptions =
    typeof options === 'function' ? { onApplied: options } : options ?? {};
  const onApplied = opts.onApplied;
  const onProposalToPage = opts.onProposalToPage;
  const preserveTimelineOnPageApply = opts.preserveTimelineOnPageApply ?? false;
  const [phase, setPhase] = useState<AssistantPhase>('idle');
  const [timeline, setTimeline] = useState<ChatTimelineItem[]>([]);
  const [proposal, setProposal] = useState<AiProposal | null>(null);
  const [rows, setRows] = useState<DiffRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef<string | null>(null);

  const showProposal = useCallback(
    async (p: AiProposal) => {
      if (onProposalToPage) {
        setPhase('applying');
        setError(null);
        try {
          await onProposalToPage(p);
          const refs = p.items.map((it) => it.target.ref);
          await aiAgentApi
            .proposalAction(p.proposal_id, 'applied', {
              item_refs: refs,
              save_endpoint: 'ui-append',
              result_ok: true,
            })
            .catch(() => undefined);
          onApplied?.();
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Không thêm được vào trang');
        }
        if (!preserveTimelineOnPageApply) setTimeline([]);
        setPhase('idle');
        return;
      }
      const cur = await adapter.load();
      const diffRows = adapter.diff(p, cur);
      setProposal(p);
      setRows(diffRows);
      setSelected(defaultSelection(diffRows));
      setEdits({});
      setPhase('review');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('bsc-ai-proposal-ready', { detail: { proposal: p } }));
      }
    },
    [adapter, onApplied, onProposalToPage, preserveTimelineOnPageApply],
  );

  const onEvent = useCallback(
    (event: AiHubEvent) => {
      const d = event.data as Record<string, unknown>;
      const upsert = (key: string, patch: Partial<ChatTimelineItem>) =>
        setTimeline((prev) => {
          const i = prev.findIndex((x) => x.key === key);
          if (i === -1) return [...prev, { key, label: String(patch.label ?? key), status: 'running', ...patch }];
          const next = [...prev];
          next[i] = { ...next[i], ...patch };
          return next;
        });
      switch (event.type) {
        case 'node.started':
          upsert(String(d.node), { label: String(d.label ?? d.node), status: 'running' });
          break;
        case 'node.finished':
          upsert(String(d.node), { status: d.ok === false ? 'error' : 'done' });
          break;
        case 'child.started':
          upsert(`child-${d.agent}`, { label: `Chuyên gia ${agentLabel(String(d.agent))}`, detail: 'đang phân tích', status: 'running' });
          break;
        case 'child.finished':
          upsert(`child-${d.agent}`, { status: d.ok ? 'done' : 'error', detail: d.ok ? `hoàn tất · ${d.items ?? 0} gợi ý` : String(d.error ?? '') });
          break;
        case 'critic': {
          const issues = (d.issues as string[]) ?? [];
          upsert(`critic-${d.round}`, {
            label: d.pass ? 'Rà soát: đạt yêu cầu' : `Rà soát: cần chỉnh ${issues.length} điểm`,
            status: 'done',
            detail: d.pass ? undefined : issues.slice(0, 2).join('; '),
          });
          break;
        }
        case 'proposal':
          void showProposal(d as unknown as AiProposal);
          break;
        case 'error':
          setError(String(d.message ?? 'Lỗi'));
          break;
        default:
          break;
      }
    },
    [showProposal],
  );

  const propose = useCallback(
    async (instruction: string, extraSnapshot?: Record<string, unknown>) => {
      setError(null);
      setTimeline([]);
      setProposal(null);
      setRows([]);
      setPhase('running');
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const cur = await adapter.load();
        const snapshot = { ...adapter.buildSnapshot(cur), ...extraSnapshot };
        const context = adapter.buildContext ? await adapter.buildContext().catch(() => ({})) : {};
        await streamRun(
          {
            kind: 'step',
            agent_id: adapter.key,
            snapshot,
            // requestHeader: engine đọc dữ liệu bước khi snapshot thiếu (nguồn `bsc`)
            context: { ...context, request_header: usePrivateStore.getState().getRequestHeader() },
            input: instruction,
          },
          { signal: controller.signal, onEvent, onRunStarted: ({ run_id }) => (runIdRef.current = run_id) },
        );
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof AiHubStreamError || e instanceof Error ? e.message : 'Không tạo được đề xuất');
        }
      } finally {
        abortRef.current = null;
        runIdRef.current = null;
        setPhase((p) => (p === 'running' ? 'idle' : p));
      }
    },
    [adapter, onEvent],
  );

  const resume = useCallback(async () => {
    try {
      const res = await aiAgentApi.latestProposal(adapter.key);
      if (res?.success && res.data?.payload) {
        await showProposal(res.data.payload);
        return true;
      }
    } catch {
      /* không có đề xuất đang chờ */
    }
    return false;
  }, [adapter.key, showProposal]);

  const toggle = useCallback((ref: string, on?: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const want = on ?? !next.has(ref);
      if (want) next.add(ref);
      else next.delete(ref);
      return next;
    });
  }, []);

  const apply = useCallback(async (opts?: { all?: boolean }) => {
    const refs = opts?.all
      ? rows.filter((r) => r.op !== 'delete' && !r.warning).map((r) => r.ref)
      : Array.from(selected);
    if (!proposal || refs.length === 0) return null;
    setPhase('applying');
    setError(null);
    try {
      const result = await adapter.apply(proposal, refs, edits);
      await aiAgentApi
        .proposalAction(proposal.proposal_id, 'applied', {
          item_refs: refs,
          saved_ids: result.savedIds,
          total_items: proposal.items.length,
          save_endpoint: result.saveEndpoint,
        })
        .catch(() => undefined);
      setPhase('applied');
      markStepChanged(adapter.key.split('.')[0]);
      onApplied?.();
      return result;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Áp dụng thất bại';
      setError(msg);
      setPhase('review');
      await aiAgentApi
        .proposalAction(proposal.proposal_id, 'applied', { item_refs: refs, result_ok: false, error: msg })
        .catch(() => undefined);
      return null;
    }
  }, [adapter, edits, onApplied, proposal, rows, selected]);

  const undo = useCallback(
    async (result: ApplyResult) => {
      await result.undo();
      if (proposal) await aiAgentApi.proposalAction(proposal.proposal_id, 'undone').catch(() => undefined);
      onApplied?.();
    },
    [onApplied, proposal],
  );

  const reject = useCallback(async () => {
    if (proposal) await aiAgentApi.proposalAction(proposal.proposal_id, 'rejected').catch(() => undefined);
    setProposal(null);
    setRows([]);
    setPhase('idle');
  }, [proposal]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setPhase('idle');
    setTimeline([]);
    setProposal(null);
    setRows([]);
    setError(null);
  }, []);

  return {
    phase,
    timeline,
    proposal,
    rows,
    selected,
    edits,
    error,
    propose,
    resume,
    // Dừng: huỷ run trên engine (ngắt kết nối không còn huỷ run) rồi ngừng nghe
    stop: () => {
      if (runIdRef.current) void agentRunClient.cancel(runIdRef.current).catch(() => undefined);
      abortRef.current?.abort();
    },
    toggle,
    selectAll: (on) => setSelected(on ? new Set(rows.map((r) => r.ref)) : new Set()),
    edit: (ref, text) => setEdits((prev) => ({ ...prev, [ref]: text })),
    apply,
    undo,
    reject,
    reset,
  };
};
